import {
  Alert,
  EAlertVariant,
  EHeadingStyleVariant,
  EIconButtonSize,
  EIconButtonVariant,
  EModalSize,
  EModalVariants,
  ETextStyleVariant,
  GeneralCloseIcon,
  GeneralIntelligenceIcon,
  GeneralRefreshRefreshCcwIcon,
  Heading,
  IconButton,
  Modal,
  Text,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import ScoutIcon from '@/assets/chatbot/scout-sand-circle.png';
import { useBreakpointContext } from '@/components/common/BreakpointContext';

import ChatbotInput from '../ChatbotInput/ChatbotInput';
import ChatbotMessages from '../ChatbotMessage/ChatbotMessage';
import { EChatbotVariant, useChatbot } from '../ChatbotProvider';

const ChatbotContent = () => {
  const intl = useIntl();

  const { isOpen, messages, setIsOpen, resetChat, error, variant } = useChatbot();
  const mostRecentMessage = messages[messages.length - 1];
  const disableSend = mostRecentMessage?.role === 'model' && mostRecentMessage?.loading;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const { isAboveTablet, isMobile } = useBreakpointContext();

  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      }, 0);
    }
  }, []);

  const checkOverflow = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollHeight, clientHeight } = scrollContainerRef.current;
    setHasOverflow(scrollHeight > clientHeight);
  }, []);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    checkOverflow();
  }, [checkOverflow]);

  const messageContentHash = messages.map(m => `${m.text}-${m.loading}`).join('|');

  useEffect(() => {
    scrollToBottom();
    checkOverflow();
  }, [messageContentHash, scrollToBottom, checkOverflow]);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      checkOverflow();
    }
  }, [isOpen, scrollToBottom, checkOverflow]);

  const headerAndActions = (
    <div className="sticky top-0 flex flex-row items-center justify-between p-3 border-b border-gray-200">
      <div className="flex flex-row items-center gap-3">
        {!isMobile && <Image src={ScoutIcon} width={40} height={40} alt="Scout icon" />}
        <Heading className="text-gray-800" variant={EHeadingStyleVariant.H6}>
          {intl.formatMessage({
            defaultMessage: 'Search with Scout',
            id: 'QhhabP',
          })}
        </Heading>
      </div>
      <div>
        <IconButton
          onClick={resetChat}
          icon={GeneralRefreshRefreshCcwIcon}
          size={EIconButtonSize.Small}
          variant={EIconButtonVariant.Ghost}
          focusRing={false}
          aria-label="Restart messages"
        />
        <IconButton
          onClick={() => setIsOpen(false)}
          icon={GeneralCloseIcon}
          size={EIconButtonSize.Small}
          variant={EIconButtonVariant.Ghost}
          focusRing={false}
          aria-label="Close chatbot"
        />
      </div>
    </div>
  );

  const content = (
    <>
      <div ref={scrollContainerRef} className="flex-1 overflow-auto" onScroll={handleScroll}>
        <div className="flex flex-col items-center justify-center pt-2">
          <GeneralIntelligenceIcon width={20} height={20} className="text-gray-700" />
          <Text
            variant={ETextStyleVariant.LegalRegular}
            className="leading-relaxed text-center text-gray-700">
            {intl.formatMessage({
              defaultMessage: 'Scout is an AI assistant.',
              id: 'hHwGDg',
            })}
          </Text>
          <Text
            variant={ETextStyleVariant.LegalRegular}
            className="leading-relaxed text-center text-gray-700">
            {intl.formatMessage({
              defaultMessage: "We're saving your conversation to improve Scout over time.",
              id: '4s644/',
            })}
          </Text>
        </div>
        <ChatbotMessages />
      </div>
      {error ? (
        <div
          className={cn('flex flex-col items-start p-4 pb-0', {
            'border-t border-gray-200': hasOverflow,
          })}>
          <Alert
            variant={EAlertVariant.Action}
            content={intl.formatMessage({
              defaultMessage:
                "Scout can't connect right now. Please try resending your message or restarting the chat.",
              id: 'QP2wTY',
            })}
            className="w-full"
          />
        </div>
      ) : (
        <div className={cn('mt-auto', { 'border-t border-gray-200': hasOverflow })}>
          <ChatbotInput disableSend={disableSend} />
        </div>
      )}
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      size={EModalSize.Large}
      variant={variant === EChatbotVariant.SRP && isAboveTablet ? undefined : EModalVariants.Bottom}
      dialogClassName={cn('h-[80vh] max-h-[600px] md:max-w-[700px]', {
        '!right-3 !left-auto !bottom-3 !rounded-2xl': variant === EChatbotVariant.HOME && !isMobile,
      })}>
      <Modal.Header className="p-0">{headerAndActions}</Modal.Header>
      <Modal.Content className="flex flex-col h-full p-0">{content}</Modal.Content>
    </Modal>
  );
};

export default ChatbotContent;
